import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../../logo.png';
import lema from '../../lema.png';
import { Phone, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [telefono, setTelefono] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(telefono, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar sesión.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #2a0710 0%, #5c1022 45%, #7a1737 100%)' }}>

      {/* ── Círculos decorativos animados de fondo ── */}
      <div className="absolute top-0 -left-40 w-96 h-96 rounded-full opacity-25 blur-3xl animate-float"
        style={{ background: 'radial-gradient(circle, #b8304f 0%, transparent 70%)' }}></div>
      <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl animate-float-reverse"
        style={{ background: 'radial-gradient(circle, #f0a3bc 0%, transparent 70%)' }}></div>
      <div className="absolute top-1/3 right-10 w-72 h-72 rounded-full opacity-15 blur-3xl animate-float"
        style={{ background: 'radial-gradient(circle, #e5a3b0 0%, transparent 70%)' }}></div>

      {/* ── Patrón de puntos sutil ── */}
      <div className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

      {/* ── Card principal ── */}
      <div className="relative w-full max-w-md animate-fade-in-up">
        <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl border border-white/40 p-8 md:p-10">

          {/* Logo + Lema */}
          <div className="text-center mb-8">
            <img src={logo} alt="DOMINIUM" className="w-64 mx-auto mb-3 drop-shadow-sm" />
            <img src={lema} alt="Zacatlán Merece Más" className="w-48 mx-auto opacity-90" />
          </div>

          {/* Título de bienvenida */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Bienvenido de nuevo</h1>
            <p className="text-sm text-slate-500">Ingresa tus credenciales para continuar</p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Teléfono */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                Teléfono
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone size={18} className="text-slate-400" />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10 dígitos"
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-guinda-500 focus:border-guinda-500 focus:bg-white transition-all duration-200"
                  required
                  autoComplete="tel"
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock size={18} className="text-slate-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-11 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-guinda-500 focus:border-guinda-500 focus:bg-white transition-all duration-200"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm animate-fade-in-up">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Botón */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 group"
              style={{ background: 'linear-gradient(135deg, #b8304f 0%, #5c1022 100%)' }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Ingresando...
                </>
              ) : (
                <>
                  Ingresar al sistema
                </>
              )}
            </button>

          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-center text-slate-400 text-xs">
              DOMINIUM © 2026 — Zacatlán, Puebla
              <span className="mx-1.5">·</span>
              <span className="font-medium text-slate-500">GARCOR Design</span>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
